import { SapicConnectivity, SapicFundingType, SapicProductTypeV5 } from '@app-types/api';
import {
    Connectivity,
    DiscountEnumType,
    FundingEnumType,
    FundingType,
    ObligationType,
    PlanType,
    Product,
    ProductTypeId,
} from '@app-types/product';

import { ApiDiscountMapper } from './api-discount.mapper';

export class SapicMapper {
    static mapToProduct(sapicProduct: SapicProductTypeV5): Product {
        const funding = sapicProduct.financements?.[0];

        const initialDeposit = funding?.apportInitial ?? 0;
        const sumODRDiscounts =
            sapicProduct.promotions?.reduce((sum, { reduction, type }) => {
                if (type !== DiscountEnumType.ODR) {
                    return sum;
                }
                return sum + reduction;
            }, 0) ?? 0;
        const priceWithOdr = initialDeposit - sumODRDiscounts;
        const price = priceWithOdr < 1 ? 1 : priceWithOdr;

        return {
            typeId: SapicMapper.mapSapicTypeToPlanType(sapicProduct.type),
            gencode: sapicProduct.gencode,
            url: sapicProduct.url,
            inStock: sapicProduct.estEnStock ?? false,
            name: sapicProduct.nom,
            // image: sapicProduct.image,
            categories: sapicProduct.categories,
            price,
            prices: {
                initial: sapicProduct.prix.initial,
                // subsidized: sapicProduct.prix.subventionne,
                forever: sapicProduct.prix.pourToujours,
                final: sapicProduct.prix.final,
            },
            discounts: sapicProduct.promotions?.map(ApiDiscountMapper.mapPromotionSapic) ?? [],
            products: [],
            neededElements: sapicProduct.necessiteElements?.map((el) => ({
                alternatives: el.alternatives.map((alt) => ({
                    typeCatalogue: alt.typeCatalogue,
                    commercialComponentCategories: alt.categoriesComposantCommercial,
                    identifier: alt.identifiant,
                    label: alt.libelle,
                })),
            })),
        };
    }

    static fundingMapper(funding: SapicFundingType): FundingType {
        return {
            type: funding.type,
            favorite: funding.prefere,
            initialDeposit: funding.apportInitial,
            amountToFinance: funding.montantAFinancer,
            totalFinancingCost: funding.coutTotalFinancement,
            APR: funding.tAEG,
            interestAmount: funding.montantInteret,
            fixedInterestRate: funding.tauxDebiteurFixe,
            installmentCount: funding.nbrEcheances,
            label: SapicMapper.getFundingLabels(funding),
            monthlyAmount: funding.montantMensualite,
        };
    }

    static getFundingLabels(funding: SapicFundingType): string {
        switch (funding.type) {
            case FundingEnumType.CASH:
                return 'Comptant';
            case FundingEnumType.EDP:
                return 'Facilité de paiement sans frais';
            default: {
                const fundingLabel = funding.tAEG === 0 ? `${funding.tAEG}%` : '';
                return `Financement smartphone ${fundingLabel}`;
            }
        }
    }

    static mapToPlan(apiData: SapicProductTypeV5): PlanType {
        const product = SapicMapper.mapToProduct(apiData);
        const planData: Partial<PlanType> = {
            gencode: apiData.gencode,
            price: apiData.prix.initial,
            products: [],
            obligation: apiData?.dureeEngagement || ObligationType.NONE,
            obligationLabel: SapicMapper.getObligationLabel(apiData?.dureeEngagement),
            name: apiData.nom.replace('Avantages Smartphone', ''),
            dataEnvelope: apiData.libelleEnveloppeData?.trim()?.replace('Go', ' Go') || '',
            connectivity: SapicMapper.mapSapicConnectivityToConnectivity(apiData.connectivite),
            technology: apiData.technologie,
        };

        return { ...product, ...planData } as PlanType;
    }

    static getObligationLabel(obligation?: ObligationType) {
        switch (obligation) {
            case ObligationType.MONTHLY_24:
                return 'engagement 24 mois';
            case ObligationType.MONTHLY_12:
                return 'engagement 12 mois';
            default:
                return 'sans engagement';
        }
    }

    static mapSapicConnectivityToConnectivity(connectivity: SapicConnectivity | undefined): Connectivity | undefined {
        switch (connectivity) {
            case SapicConnectivity.FIVE_G:
                return Connectivity.FIVE_G;
            case SapicConnectivity.FOUR_G_PLUS:
                return Connectivity.FOUR_G_PLUS;
            default:
                return Connectivity.FOUR_G;
        }
    }

    static mapSapicTypeToPlanType(type: string): ProductTypeId {
        const typeValues = Object.values(ProductTypeId);
        const typeValue = typeValues.find((value) => value === type);
        return typeValue || ProductTypeId.DEFAULT;
    }
}
